<template>
  <main class="transport-racks-redesign">
    <header class="transport-shell-header">
      <div class="header-copy">
        <p>Web rack logistics</p>
        <h1>Transport Racks</h1>
      </div>
      <div class="header-note">
        <span>Cloud transfers</span>
        <strong>External receiver labs</strong>
      </div>
    </header>

    <section class="transport-stage" aria-label="Transport rack workspace">
      <section class="transport-panel">
        <header class="panel-header">
          <div>
            <p>Transport Tracking</p>
            <h2>Current rack shipments</h2>
          </div>
          <div class="segmented-tabs" role="tablist" aria-label="Transport log type">
            <button type="button" :class="{ active: activeTransportPanel == 'pending' }" @click="activeTransportPanel = 'pending'">
              Pending <span>{{ transportlogs.length }}</span>
            </button>
            <button type="button" :class="{ active: activeTransportPanel == 'completed' }" @click="activeTransportPanel = 'completed'">
              Completed <span>{{ transportlogshistorical.length }}</span>
            </button>
          </div>
        </header>

        <div class="transport-log-list">
          <template v-if="activeTransportPanel == 'pending'">
            <article v-if="transportlogs.length == 0" class="empty-state">No pending transports.</article>
            <article v-for="log in transportlogs" :key="log.id" class="transport-log-card">
              <div class="log-rack">
                <span class="rack-icon material-symbols-outlined" aria-hidden="true">inventory_2</span>
                <div>
                  <strong>{{ log.rackId?.customId ?? log.rackId?.serialNo ?? "Transport rack" }}</strong>
                  <small>Pending acceptance</small>
                </div>
              </div>
              <div class="log-route">
                <span class="direction-pill" :class="{ incoming: log.receiving, outgoing: log.sending }">
                  {{ log.receiving ? "Incoming" : log.sending ? "Outgoing" : "Transfer" }}
                </span>
                <span>
                  {{ log.receiving ? "from" : log.sending ? "to" : "with" }}
                  {{ log.receiving ? log.senderLab.name : log.sending ? log.receiverLab.name : "lab" }}
                </span>
              </div>
              <div class="status-chip pending"><span></span>Pending</div>
              <div class="log-extras">
                <div v-if="log.comments" class="detail-popover" @mouseover="showBox(log.id, 'comment')" @mouseleave="showBox(log.id, 'comment', true)">
                  <button type="button" class="ghost-command">Comments</button>
                  <div :data-comments="log.id" class="hidden floating-detail">{{ log.comments }}</div>
                </div>
                <div v-if="log.shipping" class="detail-popover" @mouseover="showBox(log.id, 'ship')" @mouseleave="showBox(log.id, 'ship', true)">
                  <button type="button" class="ghost-command">Shipping</button>
                  <div :data-shipping="log.id" class="hidden floating-detail">
                    <strong>{{ log.shipping?.courier }}</strong>
                    <span>{{ log.shipping?.trackingid }}</span>
                  </div>
                </div>
              </div>
              <div class="log-actions">
                <button
                  :data-transport-id="log.id"
                  :data-rack-id="log.rackId?.id"
                  class="transport-action"
                  :class="{ 'bg-red-700 cancel': log.sending, 'bg-green-700 accept': log.receiving }"
                  @click="acceptOrCancel"
                >
                  {{ log.receiving ? "Accept" : log.sending ? "Cancel" : "Update" }}
                </button>
              </div>
            </article>
          </template>

          <template v-if="activeTransportPanel == 'completed'">
            <article v-if="transportlogshistorical.length == 0" class="empty-state">No completed transports.</article>
            <article v-for="log in transportlogshistorical" :key="log.id" class="transport-log-card completed">
              <div class="log-rack">
                <span class="rack-icon material-symbols-outlined" aria-hidden="true">inventory_2</span>
                <div>
                  <strong>{{ log.rackId?.customId ?? log.rackId?.serialNo ?? "Transport rack" }}</strong>
                  <small>Transport record</small>
                </div>
              </div>
              <div class="log-route">
                <span class="direction-pill" :class="{ incoming: log.receiving, outgoing: log.sending }">
                  {{ log.receiving ? "Incoming" : log.sending ? "Outgoing" : "Transfer" }}
                </span>
                <span>
                  {{ log.receiving ? "from" : log.sending ? "to" : "with" }}
                  {{ log.receiving ? log.senderLab.name : log.sending ? log.receiverLab.name : "lab" }}
                </span>
              </div>
              <div class="status-chip" :class="log.status == 'accepted' ? 'delivered' : 'cancelled'">
                <span></span>{{ log.status == "accepted" ? "Accepted" : "Cancelled" }}
              </div>
              <div class="log-extras">
                <div v-if="log.comments" class="detail-popover" @mouseover="showBox(log.id, 'comment')" @mouseleave="showBox(log.id, 'comment', true)">
                  <button type="button" class="ghost-command">Comments</button>
                  <div :data-comments="log.id" class="hidden floating-detail">{{ log.comments }}</div>
                </div>
                <div v-if="log.shipping" class="detail-popover" @mouseover="showBox(log.id, 'ship')" @mouseleave="showBox(log.id, 'ship', true)">
                  <button type="button" class="ghost-command">Shipping</button>
                  <div :data-shipping="log.id" class="hidden floating-detail">
                    <strong>{{ log.shipping?.courier }}</strong>
                    <span>{{ log.shipping?.trackingid }}</span>
                  </div>
                </div>
              </div>
            </article>
          </template>
        </div>
      </section>

      <section class="transport-panel workflow-panel">
        <header class="panel-header">
          <div>
            <p>Load And Send</p>
            <h2>Transport a rack</h2>
          </div>
          <button type="button" class="primary-command" @click="showRack = !showRack">{{ !showRack ? "Load Rack" : "Collapse Rack" }}</button>
        </header>

        <div v-show="showRack" class="workflow-card rack-workflow">
          <section class="rack-selector-card">
            <header><span class="step-marker">01</span><div><h3>Transport rack</h3><p>Select the transport rack that will receive pucks.</p></div></header>
            <label>
              <span>Transport rack</span>
              <input list="transport-list" type="text" ref="transportRack" @focus="clearValue($event)" @change="loadLeftRackPucks" placeholder="Select a transport rack" />
            </label>
            <datalist id="transport-list" ref="trydatalist">
              <option v-for="rack in transportRacks" :data-rack-id="rack.id" :key="rack.id" :value="rack.customId ?? rack.serialNo"></option>
            </datalist>
            <div class="transport-pucks puck-frame">
              <transport-item :side="'left'" @show-puck="showPuck" :left-has-selected-rack="leftHasSelectedRack" :array-of-pucks="pucksForLeft"></transport-item>
            </div>
          </section>

          <div class="transfer-bridge" aria-label="Puck transfer direction">
            <span class="material-symbols-outlined" aria-hidden="true">keyboard_double_arrow_left</span>
            <strong>Transfer pucks</strong>
            <small>Add pucks into the transport rack</small>
          </div>

          <section class="rack-selector-card">
            <header><span class="step-marker">02</span><div><h3>Available rack</h3><p>Select a rack to move pucks from.</p></div></header>
            <label>
              <span>Rack</span>
              <input list="allracks-list" type="text" ref="allRack" @focus="clearValue($event)" @change="loadRightRackPucks" placeholder="Select a rack" />
            </label>
            <datalist id="allracks-list">
              <option v-for="rack in allRacks" :data-rack-id="rack.id" :key="rack.id" :value="rack.customId ?? rack.serialNo"></option>
            </datalist>
            <div class="all-rack-pucks puck-frame">
              <transport-item @show-puck="showPuck" :disabled="pucksForLeft.length >= 10" :side="'right'" :torackid="activeTransportRackId ?? true" :right-has-selected-rack="rightHasSelectedRack" :array-of-pucks="pucksForRight"></transport-item>
            </div>
          </section>
        </div>

        <section class="puck-detail-card" v-if="showPuckSection">
          <puck-section :active-puck="activePuckRef" :puck-data-change="puckDataChange" :reset-puck-selected="resetPuckSection" @new-puck-selected="resetPuckSection = false" @reset-puck-change="puckDataChange = false" :puck-data="activePuckData"></puck-section>
          <div id="prepuck"></div>
        </section>

        <div class="detail-grid">
          <section class="workflow-card collapsible-card">
            <button type="button" class="card-toggle" @click="showComments = !showComments">
              <span class="material-symbols-outlined" aria-hidden="true">notes</span><span>Comments</span><strong>{{ !showComments ? "Add" : "Collapse" }}</strong>
            </button>
            <div v-show="showComments" class="card-body"><label><span>Transport comments</span><textarea ref="comments" rows="6" placeholder="Add handling notes or receiving context"></textarea></label></div>
          </section>

          <section class="workflow-card collapsible-card">
            <button type="button" class="card-toggle" @click="showShipping = !showShipping">
              <span class="material-symbols-outlined" aria-hidden="true">local_shipping</span><span>Shipping info</span><strong>{{ !showShipping ? "Add" : "Collapse" }}</strong>
            </button>
            <div v-show="showShipping" class="card-body two-fields">
              <label><span>Courier</span><select ref="courier"><option value="" selected="true" disabled="disabled">Select Courier</option><option value="dhl">DHL</option><option value="fedex">FedEx</option><option value="ups">UPS</option><option value="usps">USPS</option></select></label>
              <label><span>Tracking number</span><input ref="trackingNo" type="text" placeholder="Tracking number" /></label>
            </div>
          </section>
        </div>

        <section class="workflow-card destination-card">
          <div class="rack-summary">
            <span>Transporting rack</span>
            <strong :class="{ missing: !$refs.transportRack?.value }">{{ $refs.transportRack?.value ? $refs.transportRack?.value : "No transport rack loaded" }}</strong>
          </div>
          <div class="destination-manager">
            <div class="destination-form-row">
              <label><span>Receiving lab</span><select ref="receivinglab"></select></label>
              <div class="destination-add-form">
                <label><span>Add a lab</span><input id="labcode" ref="labcode" type="text" placeholder="Enter lab code" /></label>
                <button type="button" class="secondary-command" @click="addLab"><button-inner>Add</button-inner></button>
                <div class="fadeoutbutton">Lab added!</div>
              </div>
            </div>
          </div>
        </section>

        <div class="submit-row">
          <button @click="transportRackMethod" :class="[!transportlabval || !receivinglabval ? 'buttondisabled' : '']" class="transport-submit relative">
            <button-inner>Transport Rack</button-inner>
          </button>
          <div class="fadeoutbutton"></div>
        </div>
      </section>
    </section>
  </main>
</template>
<script>
import TransportItem from "./TransportItem.vue";
import PuckSection from "./manage-products-sections/PuckSection.vue";
import HeaderBar from "./manage-products-sections/HeaderBar.vue";
import ShowButton from "../../ui/TransportShowButton.vue";
export default {
  components: { HeaderBar, ShowButton, TransportItem, PuckSection },
  data() {
    return {
      rightHasSelectedRack: false,
      leftHasSelectedRack: false,
      transportlabval: false,
      receivinglabval: false,
      showComments: false,
      showShipping: false,
      showRack: false,
      allDewars: [],
      transportRacks: [],
      transportlogs: [],
      transportlogshistorical: [],
      activePuckRef: null,
      puckDataChange: false,
      resetPuckSection: false,
      activePuckData: [],
      activePuckId: null,
      activeRackId: null,
      activePuckSide: null,
      activeTransportRackId: null,
      pucksForLeft: [],
      pucksForRight: [],
      activeTransportPanel: "pending",
      showPuckSection: false,
    };
  },

  watch: {
    allRacks() {
      if (this.pucksForRight.length > 0 || this.pucksForLeft.length > 0) {
        console.log("it changed");
        this.loadRightRackPucks();

        this.getTransportRacksOnly(true);
        this.updateActivePuckData();
      }
    },
    transportUpdate() {
      //this is for when a puck moves to a  rack
      if (this.appStore.state.transportUpdate) {
        this.getTransportRacksOnly(true);
        this.getDewarsOldWay(true);

        this.appStore.state.transportUpdate = false;
      }
    },
    transportrefresh() {
      //for refreshing the puck after a svaae ;has been made
      if (this.appStore.state.refreshtransport) {
        console.log("yes it changed to a value");
        this.getTransportRacksOnly(true);
        this.getDewarsOldWay(true);

        this.appStore.state.refreshtransport = false;
      }
    },
  },

  computed: {
    transportUpdate() {
      return this.appStore.state.transportUpdate;
    },
    transportrefresh() {
      return this.appStore.state.refreshtransport;
    },
    allRacks() {
      let allRacks = [];
      this.allDewars?.forEach((dewar) => {
        dewar.racks.forEach((rack) => allRacks.push(rack));
      });

      return allRacks;
    },
  },
  methods: {
    showCompletedTransports() {},

    acceptOrCancel(buttonev) {
      const isCancel = buttonev.target.classList.contains("bg-red-700");

      let body = {
        rackId: buttonev.target.closest("button").dataset.rackId,

        status: "accepted",
      };
      if (isCancel) body.status = "cancelled";

      this.appStore
        .dispatch("updateItem", {
          item: "transports",
          objid: buttonev.target.closest("button").dataset.transportId,
          body: body,
        })
        .then(() => {
          this.getTransportLogs();
          this.getTransportLogs(true);
        });
    },
    getTransportLogs(historical) {
      let url = "transports";
      if (historical) url = "transports?historical=true";
      this.appStore
        .dispatch("getAdminStuff", { url })
        .then((data) => {
          if (historical) this.transportlogshistorical = data;
          else this.transportlogs = data;
        })
        .catch((err) => console.log(err));
    },

    transportRackMethod(buttonev) {
      if (
        buttonev.target.closest("button").classList.contains("buttondisabled")
      )
        return;

      const optionValue = this.$refs.transportRack.value;
      const optionRackId = document.querySelector(
        'option[value="' + optionValue + '"]'
      )?.dataset?.rackId;
      this.appStore.state.buttonFXinit(buttonev);
      this.appStore
        .dispatch("createItem", {
          item: "transports",
          body: {
            senderLab: this.appStore.state.activeUser.labId.id,
            receiverLab: this.$refs.receivinglab.value,
            comments: this.$refs.comments.value,
            shipping: {
              courier: this.$refs.courier.value,
              trackingid: this.$refs.trackingNo.value,
            },
            rackId: optionRackId,
            status: "pending",
          },
        })
        .then(() => {
          this.getTransportLogs();
          return this.appStore.state.buttonFX(
            buttonev.target.closest("button"),
            "Transport Started"
          );
        })
        .then(() => {
          setTimeout(() => {
            this.pucksForleft = [];
            this.pucksForRight = [];
            this.$refs.allRack.value = "";
            this.$refs.transportRack.value = "";
            this.leftHasSelectedRack = false;
            this.rightHasSelectedRack = false;
            window.scrollTo(0, 0);
          }, 500);
        })
        .catch((err) => {
          return this.appStore.state.buttonFX(
            buttonev.target.closest("button"),
            "Error: " + err
          );
        });
    },
    addLab(buttonev) {
      console.log(this.appStore.state.activeUser);
      if (!this.$refs.labcode.value) return;

      this.appStore.state.buttonFXinit(buttonev);
      this.appStore
        .dispatch("updateItem", {
          item: "labs",
          objid: this.appStore.state.activeUser.labId.id,
          labcode: this.$refs.labcode.value.toUpperCase(),
          body: {},
        })

        .then(() => {
          this.getReceivingLabsList(this.$refs.labcode.value);
          return this.appStore.state.buttonFX(
            buttonev.target.closest("button"),
            "Added lab!"
          );
        })
        .catch((err) => {
          console.log(err);
          return this.appStore.state.buttonFX(
            buttonev.target.closest("button"),
            err
          );
        });
    },
    clearValue(ev) {
      ev.target.value = "";
    },
    showPuck(puckData, puckRef, puckSide) {
      //function called when a transport item text button puck is clicked

      console.log(puckRef);
      this.activePuckSide = puckSide;
      this.activeRackId = puckData.rackId;
      this.activePuckId = puckData.id;
      this.activePuckRef = puckRef;
      //sets the data from the transport item
      this.activePuckData = puckData;

      this.resetPuckSection = true;
      console.log("activepuckdata", this.activePuckData);
      this.showPuckSection = true;
      setTimeout(
        () => document.getElementById("prepuck").scrollIntoView(),
        500
      );
    },
    showBox(logId, boxType, mouseOut) {
      let selector = "comments";
      if (boxType == "ship") selector = "shipping";

      let elem = document.querySelector(`[data-${selector} = "${logId}"]`);
      console.log(elem, selector);

      if (mouseOut) elem.classList.add("hidden");
      else elem.classList.remove("hidden");
    },
    updateActivePuckData() {
      console.log("here but");
      //update all the pucks called from dewarData store watcher
      console.log(this.activeDewarId, this.activeRackElemNum);
      if (this.activeRackId && this.activePuckId) {
        //need the rack id that was clicked
        //loop through the dewars find the dewar that matches the rack id and return
        let findrack;
        let thepuck;
        console.log(this.activeRackId, this.activePuckId, "ADsgadgs");

        //this will only work for right side pucks
        console.log(this.activePuckSide, "activePuckSide");
        if (this.activePuckSide == "right") {
          for (const dewar of this.allDewars) {
            findrack = dewar.racks?.filter((rack) => {
              return rack.id.toString() == this.activeRackId.toString();
            });

            if (findrack.length > 0) break;
          }
        } else if (this.activePuckSide == "left") {
          findrack = this.transportRacks?.filter((rack) => {
            return rack.id.toString() == this.activeRackId.toString();
          });
        }

        thepuck = findrack[0]?.pucks?.filter((puck) => {
          console.log(puck.id, this.activePuckId);
          return puck.id == this.activePuckId;
        });

        // this.activePuckData = findrack[0]?.pucks;
        //update the  active puck data
        //ad the puckid
        //
        this.activePuckData = thepuck[0];
        //console.log(this.activePuckData, this.showPuck);
        console.log(this.puck);

        this.puckDataChange = true;
      }
    },
    getReceivingLabsList(labcode) {
      const url =
        this.appStore.state.hostname + "/api/v1/labs/getTransportLabsList";
      const request = new Request(url, {
        credentials: "include",
        method: "GET",

        headers: { "content-type": "application/json" },
      });
      console.log(url);
      fetch(request)
        .then((resp) => resp.json())
        .then((data) => {
          if (data.status == "fail") throw new Error(data.message);
          else {
            console.log(data.data.data, "we here");
            this.$refs.receivinglab.innerHTML = "";
            if (data.data.data.length == 0) {
              const option = document.createElement("option");

              option.selected = true;
              option.disabled = true;
              option.value = "nolabs";
              option.text = "No labs added";
              this.$refs.receivinglab.appendChild(option);

              return;
            }

            data.data.data.forEach((lab) => {
              const option = document.createElement("option");
              if (labcode && labcode.toUpperCase() == lab.code)
                option.selected = true;
              option.value = lab.id;
              option.text = lab.name;
              this.$refs.receivinglab.appendChild(option);
            });
            console.log(this.$refs.receivinglab.value, "valueis");
            if (this.$refs.receivinglab.value) this.receivinglabval = true;
            else this.receivinglabval = false;
          }
        })
        .catch((err) => {
          console.log(err);
        });
    },
    getDewarsOldWay(autoLoadRight) {
      return new Promise((res, rej) => {
        const url =
          this.appStore.state.hostname +
          "/api/v1/dewars/fortransport?fortransportracks=true";
        const request = new Request(url, {
          credentials: "include",
          method: "GET",

          headers: { "content-type": "application/json" },
        });
        console.log(url);
        fetch(request)
          .then((resp) => {
            if (!resp.ok) throw new Error("Network response was not ok.");
            return resp.json();
          })
          .then((data) => {
            if (data.status == "fail") throw new Error(data.message);
            else {
              this.allDewars = data.data.data;
              console.log("this is allDewars, ", this.allDewars);
              if (autoLoadRight) {
                //notsure if this is right
                this.loadRightRackPucks();
                this.updateActivePuckData();
              }
              res();
            }
          })
          .catch((err) => {
            console.log(err);
            rej(err);
          });
      });
    },
    getTransportRacksOnly(autoLoadLeft) {
      return new Promise((res, rej) => {
        const url =
          this.appStore.state.hostname + "/api/v1/racks?transportRacksOnly=true";
        const request = new Request(url, {
          credentials: "include",
          method: "GET",

          headers: { "content-type": "application/json" },
        });
        console.log(url);
        fetch(request)
          .then((resp) => resp.json())
          .then((data) => {
            if (data.status == "fail") throw new Error(data.message);
            else {
              // console.log(data.data.data);
              //context.commit("updateUserData", { value: data.data.data });
              //arrayOfDewars.value = data.data.data;
              this.transportRacks = data.data.data;
              if (autoLoadLeft) {
                //notsure if this is right

                this.loadLeftRackPucks();
                // this.loadRightRackPucks();
                this.updateActivePuckData();
              }

              res();
            }
          })
          .catch((err) => {
            console.log(err);
            rej();
          });
      });
    },

    loadRightRackPucks() {
      const optionValue = this.$refs.allRack.value;
      //is this an issue if the values are the same how does it know which one to
      const optionRackId = document.querySelector(
        'option[value="' + optionValue + '"]'
      )?.dataset?.rackId;
      if (!optionRackId) {
        this.pucksForRight = [];
        return;
      } else {
        this.rightHasSelectedRack = true;
      }

      const rackmatch = this.allRacks.filter(
        (rack) => rack.id == optionRackId
      );

      this.pucksForRight = rackmatch[0].pucks;
      //this.getDewarsOldWay(rackmatch[0].pucks);
    },

    loadLeftRackPucks() {
      if (this.$refs.transportRack.value) this.transportlabval = true;
      else this.transportlabval = false;
      console.log(this.transportlabval, this.receivinglabval);
      const optionValue = this.$refs.transportRack.value;
      const optionRackId = document.querySelector(
        'option[value="' + optionValue + '"]'
      )?.dataset?.rackId;
      if (!optionRackId) {
        this.pucksForLeft = [];
        return;
      } else {
        this.leftHasSelectedRack = true;
      }
      this.activeTransportRackId = optionRackId;
      console.log(
        "here are the transport racks",
        this.transportRacks,
        "here is the active transport rack",
        optionRackId
      );
      const rackmatch = this.transportRacks.filter(
        (rack) => rack.id == optionRackId
      );

      this.pucksForLeft = rackmatch[0].pucks;
    },
  },
  mounted() {
    this.appStore.state.transportrefresh = false;
    var isFirefox = typeof InstallTrigger !== "undefined";

    if (isFirefox) {
      this.$refs.transportRack.placeholder += " (double click to load all)";
      this.$refs.transportRack.addEventListener(
        "select",
        this.loadLeftRackPucks
      );
      this.$refs.allRack.placeholder += " (double click to load all)";
      this.$refs.allRack.addEventListener("select", this.loadRightRackPucks);
    }
    this.getTransportLogs();
    this.getTransportLogs(true);
    this.getTransportRacksOnly();
    this.getReceivingLabsList();
    this.getDewarsOldWay();
  },
};
</script>
<style scoped>
.transport-racks-redesign {
  --mp-border: #dbe3ef;
  --mp-ink: #172033;
  display: grid;
  align-content: start;
  width: 100%;
  min-width: 0;
  min-height: calc(100vh - 24px);
  background: #fff;
  color: var(--mp-ink);
  font-family: Montserrat, Inter, system-ui, sans-serif;
}

.transport-shell-header {
  display: flex;
  justify-content: space-between;
  gap: 14px;
  align-items: center;
  min-width: 0;
  padding: 14px 18px;
  border-bottom: 1px solid var(--mp-border);
  background: #fff;
}

.header-copy p,
.header-copy h1,
.panel-header p,
.panel-header h2,
.rack-selector-card h3,
.rack-selector-card p {
  margin: 0;
}

.header-copy p,
.panel-header p {
  color: #64748b;
  font-size: 11px;
  font-weight: 900;
  text-transform: uppercase;
}

.header-copy h1 {
  color: #172033;
  font-size: 20px;
  line-height: 1.15;
}

.header-note {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 32px;
  padding: 0 12px;
  border: 1px solid #dbe3ef;
  border-radius: 999px;
  background: #f8fafc;
  color: #526173;
  font-size: 12px;
  font-weight: 800;
  white-space: nowrap;
}

.header-note span {
  color: #64748b;
  font-size: 11px;
  font-weight: 900;
  text-transform: uppercase;
}

.header-note strong {
  color: #172033;
}

.transport-stage {
  display: grid;
  gap: 12px;
  min-width: 0;
  padding: 12px;
}

.transport-panel,
.workflow-card,
.puck-detail-card {
  min-width: 0;
  border: 1px solid var(--mp-border);
  border-radius: 8px;
  background: #fff;
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  border-bottom: 1px solid var(--mp-border);
}

.panel-header h2 {
  color: #172033;
  font-size: 18px;
  line-height: 1.2;
}

.segmented-tabs {
  display: inline-flex;
  gap: 4px;
  padding: 3px;
  border: 1px solid #cbd5e1;
  border-radius: 7px;
  background: #f8fafc;
}

.segmented-tabs button,
.primary-command,
.ghost-command,
.secondary-command {
  border: 1px solid transparent;
  border-radius: 6px;
  font: inherit;
  font-size: 12px;
  font-weight: 900;
  cursor: pointer;
}

.segmented-tabs button {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  min-height: 30px;
  padding: 0 10px;
  color: #526173;
  background: transparent;
}

.segmented-tabs button.active {
  border-color: #9db4d0;
  background: #fff;
  color: #172033;
  box-shadow: 0 1px 2px rgb(15 23 42 / 0.08);
}

.segmented-tabs span {
  min-width: 22px;
  padding: 1px 6px;
  border-radius: 999px;
  background: #e7eef7;
  color: #335c8b;
  text-align: center;
}

.transport-log-list {
  display: grid;
  gap: 8px;
  padding: 10px;
}

.transport-log-card {
  display: grid;
  grid-template-columns: minmax(180px, 1.2fr) minmax(210px, 1fr) auto minmax(150px, auto) auto;
  gap: 10px;
  align-items: center;
  min-height: 68px;
  padding: 10px;
  border: 1px solid #e2e8f0;
  border-radius: 7px;
  background: #fbfdff;
}

.log-rack {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.rack-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 7px;
  background: #e7eef7;
  color: #335c8b;
}

.log-rack strong,
.rack-summary strong {
  display: block;
  overflow: hidden;
  color: #172033;
  font-size: 13px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.log-rack small {
  display: block;
  margin-top: 2px;
  color: #64748b;
  font-size: 11px;
  font-weight: 800;
}

.log-route {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  color: #526173;
  font-size: 12px;
  font-weight: 800;
}

.direction-pill,
.status-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 26px;
  padding: 0 9px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 900;
  white-space: nowrap;
}

.direction-pill.incoming {
  background: #dff7ed;
  color: #0f766e;
}

.direction-pill.outgoing {
  background: #fee2e2;
  color: #b91c1c;
}

.status-chip.pending {
  background: #fff7ed;
  color: #c2410c;
}

.status-chip.delivered {
  background: #dcfce7;
  color: #15803d;
}

.status-chip.cancelled {
  background: #fee2e2;
  color: #b91c1c;
}

.status-chip span {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: currentColor;
}

.log-extras,
.log-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  justify-content: flex-end;
}

.detail-popover {
  position: relative;
}

.ghost-command {
  min-height: 30px;
  padding: 0 10px;
  border-color: #cbd5e1;
  background: #fff;
  color: #334155;
}

.floating-detail {
  position: absolute;
  right: 0;
  top: calc(100% + 6px);
  z-index: 30;
  width: max-content;
  min-width: 260px;
  max-width: 320px;
  max-height: 50vh;
  overflow: auto;
  padding: 10px;
  border: 1px solid #9db4d0;
  border-radius: 7px;
  background: #fff;
  color: #172033;
  box-shadow: 0 16px 30px rgb(15 23 42 / 0.16);
  font-size: 12px;
  line-height: 1.4;
}

.floating-detail strong,
.floating-detail span {
  display: block;
}

.transport-action {
  min-height: 34px;
  padding: 0 12px;
  border: 1px solid transparent;
  border-radius: 6px;
  color: #fff;
  font: inherit;
  font-size: 12px;
  font-weight: 900;
  cursor: pointer;
}

.transport-action.accept {
  background: #0f766e;
}

.transport-action.cancel {
  background: #b91c1c;
}

.empty-state {
  padding: 18px;
  border: 1px dashed #cbd5e1;
  border-radius: 7px;
  background: #f8fafc;
  color: #64748b;
  font-size: 13px;
  font-weight: 800;
}

.primary-command {
  min-height: 32px;
  padding: 0 12px;
  border-color: #0f766e;
  background: #0f766e;
  color: #fff;
}

.rack-workflow {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 132px minmax(0, 1fr);
  gap: 12px;
  margin: 12px;
  padding: 12px;
  background: #f8fafc;
}

.rack-selector-card {
  min-width: 0;
  border: 1px solid #dbe3ef;
  border-radius: 8px;
  background: #fff;
}

.rack-selector-card header {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 12px;
  border-bottom: 1px solid #e2e8f0;
}

.step-marker {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 28px;
  border-radius: 6px;
  background: #e7eef7;
  color: #335c8b;
  font-size: 12px;
  font-weight: 900;
}

.rack-selector-card h3 {
  font-size: 14px;
  line-height: 1.2;
}

.rack-selector-card p {
  margin-top: 2px;
  color: #64748b;
  font-size: 12px;
  font-weight: 700;
}

.rack-selector-card label,
.card-body label,
.destination-card label {
  display: grid;
  gap: 5px;
  padding: 12px;
  color: #526173;
  font-size: 11px;
  font-weight: 900;
  text-transform: uppercase;
}

input,
select,
textarea {
  width: 100%;
  min-width: 0;
  border: 1px solid #9db4d0;
  border-radius: 7px;
  background: #fff;
  color: #172033;
  font: inherit;
  font-size: 13px;
  text-transform: none;
}

input,
select {
  height: 38px;
  padding: 0 10px;
}

textarea {
  padding: 9px 10px;
  resize: vertical;
}

input:focus,
select:focus,
textarea:focus {
  outline: none;
  border-color: #6889b3;
  box-shadow: 0 0 0 3px rgb(51 92 139 / 0.12);
}

.puck-frame {
  margin: 0 12px 12px;
  padding: 10px;
  min-height: 210px;
  overflow: auto;
  border: 1px solid #e2e8f0;
  border-radius: 7px;
  background: #f8fafc;
}

.transfer-bridge {
  display: grid;
  align-content: center;
  justify-items: center;
  gap: 4px;
  min-height: 180px;
  color: #0f766e;
  text-align: center;
}

.transfer-bridge .material-symbols-outlined {
  font-size: 42px;
}

.transfer-bridge strong {
  color: #172033;
  font-size: 13px;
}

.transfer-bridge small {
  max-width: 110px;
  color: #64748b;
  font-size: 11px;
  font-weight: 800;
  line-height: 1.3;
}

.puck-detail-card {
  margin: 12px;
  padding: 12px;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin: 12px;
}

.card-toggle {
  display: grid;
  grid-template-columns: 24px minmax(0, 1fr) auto;
  gap: 8px;
  align-items: center;
  width: 100%;
  min-height: 46px;
  padding: 0 12px;
  border: 0;
  border-radius: 8px;
  background: #fff;
  color: #172033;
  font: inherit;
  font-size: 13px;
  font-weight: 900;
  text-align: left;
  cursor: pointer;
}

.card-toggle strong {
  color: #0f766e;
  font-size: 12px;
}

.card-body {
  border-top: 1px solid #e2e8f0;
}

.two-fields,
.destination-form-row {
  display: grid;
  grid-template-columns: minmax(160px, 0.7fr) minmax(180px, 1fr);
  gap: 8px;
}

.destination-card {
  display: grid;
  grid-template-columns: minmax(220px, 0.8fr) minmax(360px, 1fr);
  gap: 10px;
  align-items: stretch;
  margin: 12px;
  padding: 12px;
}

.rack-summary {
  display: grid;
  gap: 5px;
  align-self: stretch;
  padding: 12px;
  border: 1px solid #e2e8f0;
  border-radius: 7px;
  background: #f8fafc;
  color: #526173;
  font-size: 11px;
  font-weight: 900;
  text-transform: uppercase;
}

.rack-summary .missing {
  color: #b91c1c;
}

.destination-manager {
  display: grid;
  gap: 8px;
  min-width: 0;
}

.destination-card label {
  padding: 0;
}

.destination-add-form {
  display: grid;
  grid-template-columns: minmax(180px, 1fr) auto;
  gap: 8px;
  align-items: end;
  min-width: 0;
}

.secondary-command {
  min-height: 38px;
  padding: 0 12px;
  border: 1px solid #0f766e;
  background: #0f766e;
  color: #fff;
}

.submit-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 12px;
  padding-top: 2px;
}

.transport-submit {
  min-height: 42px;
  padding: 0 18px;
  border: 1px solid #0f766e;
  border-radius: 7px;
  background: #0f766e;
  color: #fff;
  font: inherit;
  font-size: 14px;
  font-weight: 900;
  cursor: pointer;
}

.buttondisabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.hidden {
  display: none;
}

@media (max-width: 1100px) {
  .transport-log-card {
    grid-template-columns: minmax(180px, 1fr) minmax(200px, 1fr);
  }

  .status-chip,
  .log-extras,
  .log-actions {
    justify-self: start;
  }

  .rack-workflow,
  .detail-grid,
  .destination-card,
  .destination-form-row {
    grid-template-columns: 1fr;
  }

  .transfer-bridge {
    min-height: auto;
    padding: 8px;
  }
}

@media (max-width: 720px) {
  .transport-shell-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .header-note {
    white-space: normal;
  }

  .panel-header {
    align-items: stretch;
    flex-direction: column;
  }

  .segmented-tabs {
    width: 100%;
  }

  .segmented-tabs button {
    flex: 1;
    justify-content: center;
  }

  .transport-log-card,
  .two-fields {
    grid-template-columns: 1fr;
  }
}
</style>
